import { AxiosHeaders } from 'axios';
import { getClientCredentialsToken } from '@digiwedge/oauth-auth-utils';
import { logger, logError } from '@digiwedge/logger';
import { axiosClient } from './axiosClient.js';
import { ExternalAPIError } from '@digiwedge/error-utils';
const getEnv = (key) => typeof process !== 'undefined' && process.env ? process.env[key] : undefined;
const getEnvAny = (...keys) => {
    for (const k of keys) {
        const v = getEnv(k);
        if (typeof v === 'string' && v.length > 0)
            return v;
    }
    return undefined;
};
// Simple in-process token cache with singleflight
let tokenCache = null;
let inflightToken = null;
function isTokenValid() {
    if (!tokenCache)
        return false;
    const skewMs = 60_000; // refresh 60s early
    return Date.now() + skewMs < tokenCache.expiresAt;
}
async function fetchTokenWithCache(oauthConfig) {
    if (isTokenValid() && tokenCache)
        return { accessToken: tokenCache.accessToken };
    if (!inflightToken) {
        inflightToken = getClientCredentialsToken(oauthConfig).finally(() => {
            setTimeout(() => {
                inflightToken = null;
            }, 0);
        });
    }
    const res = await inflightToken;
    if (res?.accessToken && typeof res.expiresIn === 'number') {
        tokenCache = { accessToken: res.accessToken, expiresAt: Date.now() + res.expiresIn * 1000 };
    }
    return res;
}
/**
 * Axios request interceptor: attach OAuth2 client credentials token to headers.
 */
export async function requestHandler(config) {
    const clientId = getEnvAny('AXIZ_CLIENT_ID');
    const clientSecret = getEnvAny('AXIZ_CLIENT_SECRET', 'AXIZ_CLIENT_ID_SECRET');
    const tokenUrl = getEnvAny('AXIZ_DIGITAL_IDENT');
    const scopeEnv = getEnvAny('AXIZ_SCOPE');
    const scope = scopeEnv ? scopeEnv.split(/\s+/) : undefined;
    if (!clientId || !clientSecret || !tokenUrl) {
        return config;
    }
    const oauthConfig = {
        clientId,
        clientSecret,
        tokenUrl,
        scope,
    };
    let tokenResponse;
    try {
        tokenResponse = await fetchTokenWithCache(oauthConfig);
    }
    catch (err) {
        const fetchError = err instanceof Error ? err : new Error(JSON.stringify(err));
        logger.error('Failed to fetch OAuth token', { fetchError });
        logError(logger, 'Failed to fetch OAuth token', { fetchError });
        return config;
    }
    const token = tokenResponse?.accessToken;
    if (token) {
        const headers = new AxiosHeaders(config.headers ?? {});
        headers.set('Authorization', `Bearer ${token}`);
        config.headers = headers;
    }
    return config;
}
/**
 * Axios response interceptor: on 401 attempts token refresh once, otherwise throws.
 */
export async function responseHandler(error) {
    const axiosError = error;
    const originalRequest = axiosError.config;
    if (axiosError.response?.status === 401 && !originalRequest._retry) {
        return handleTokenRefreshAndRetry(originalRequest);
    }
    return handleExternalAPIError(axiosError, originalRequest);
}
async function handleTokenRefreshAndRetry(originalRequest) {
    logger.warn('401 Unauthorized - refreshing token and retrying request');
    originalRequest._retry = true;
    const clientId = getEnvAny('AXIZ_CLIENT_ID');
    const clientSecret = getEnvAny('AXIZ_CLIENT_SECRET', 'AXIZ_CLIENT_ID_SECRET');
    const tokenUrl = getEnvAny('AXIZ_DIGITAL_IDENT');
    const scopeEnv = getEnvAny('AXIZ_SCOPE');
    if (!clientId || !clientSecret || !tokenUrl) {
        return Promise.reject(new Error('Missing required OAuth environment variables'));
    }
    const oauthConfig = {
        clientId,
        clientSecret,
        tokenUrl,
        scope: scopeEnv ? scopeEnv.split(/\s+/) : undefined,
    };
    try {
        const tokenResponse = await getClientCredentialsToken(oauthConfig);
        const token = tokenResponse?.accessToken;
        if (token) {
            const headers = new AxiosHeaders(originalRequest.headers ?? {});
            headers.set('Authorization', `Bearer ${token}`);
            originalRequest.headers = headers;
        }
        // clear cache on 401-triggered refresh to avoid reusing a bad token
        tokenCache = null;
        await requestHandler(originalRequest);
        return axiosClient.request(originalRequest);
    }
    catch (err) {
        const refreshError = err instanceof Error ? err : new Error(JSON.stringify(err));
        logger.error('Token refresh failed', { refreshError });
        logError(logger, 'Token refresh failed', { refreshError });
        return Promise.reject(refreshError);
    }
}
function handleExternalAPIError(axiosError, originalRequest) {
    const endpoint = axiosError.config?.url ?? axiosClient.defaults.baseURL ?? 'unknown';
    const responseData = axiosError.response?.data;
    const requestParams = (originalRequest.data ?? originalRequest.params);
    const statusCode = axiosError.response?.status ?? 500;
    const retryable = statusCode >= 500 && statusCode < 600;
    logError(logger, 'External API call failed', {
        error: axiosError.toJSON?.() ?? axiosError,
    });
    throw new ExternalAPIError('External API error', endpoint, responseData, requestParams, retryable, statusCode);
}
//# sourceMappingURL=axiosClientHandlers.js.map